/**
 * Peak-power readout for the Power panel — the dyno-style figure, not the
 * single highest sample.
 *
 * Backend values are HP. Two stages:
 *   1. dynoPullSamples — keep only what a dyno would measure: wide-open throttle
 *      in a straight line. Falls back to every positive sample when the channels
 *      aren't logged or the gate leaves too few.
 *   2. robustEnvelopePeak — highest p98 across 500-rpm bins (the peak of the
 *      power-vs-RPM envelope), else the p98 of all samples without RPM.
 */
export function percentile(sorted, p) {
  const n = sorted.length;
  if (!n) return null;
  const idx = (p / 100) * (n - 1);
  const lo = Math.floor(idx), hi = Math.ceil(idx);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

// A dyno pull is wide-open throttle in a straight line. Keep only those samples
// when the channels exist: part-throttle and cornering samples (tyre scrub,
// lifted throttle) only dilute the envelope, and the transition into a brake
// zone is where the raw max used to land. Throttle scale is never assumed —
// "WOT" is 90 % of the lap's own throttle range, as the corner metrics do.
const WOT_FRAC = 0.9;
const STRAIGHT_MAX_LAT_G = 0.3;
const MIN_GATED_SAMPLES = 50;

export function dynoPullSamples(pts) {
  const thr = pts.map(p => p.throttle).filter(v => v != null && Number.isFinite(v));
  const hasThr = thr.length > MIN_GATED_SAMPLES;
  const thrMax = hasThr ? Math.max(...thr) : null;
  const thrMin = hasThr ? Math.min(...thr) : null;
  const wot = hasThr && thrMax > thrMin ? thrMin + WOT_FRAC * (thrMax - thrMin) : null;
  const hasLat = pts.some(p => p.lat_g != null);
  const kept = pts.filter(p =>
    (wot == null || (p.throttle != null && p.throttle >= wot))
    && (!hasLat || p.lat_g == null || Math.abs(p.lat_g) <= STRAIGHT_MAX_LAT_G));
  return kept.length >= MIN_GATED_SAMPLES ? { pts: kept, gated: wot != null || hasLat } : { pts, gated: false };
}

// Dyno-style "peak power": the highest p98 across 500-rpm bins (peak of the
// power-vs-RPM envelope), falling back to the p98 of all positive samples when
// RPM isn't logged. Robust to the single-sample transients that the raw max
// catches — mirrors the leaderboard's _robust_peak philosophy.
export function robustEnvelopePeak(pts) {
  const withRpm = pts.filter(p => p.rpm != null);
  if (withRpm.length > 50) {
    const bins = new Map();
    for (const p of withRpm) {
      const b = Math.floor(p.rpm / 500);
      if (!bins.has(b)) bins.set(b, []);
      bins.get(b).push(p.channel_value);
    }
    let best = null;
    for (const arr of bins.values()) {
      if (arr.length < 20) continue;
      arr.sort((a, b) => a - b);
      const v = percentile(arr, 98);
      if (best == null || v > best) best = v;
    }
    if (best != null) return best;
  }
  const all = pts.map(p => p.channel_value).sort((a, b) => a - b);
  return percentile(all, 98);
}
